import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { useBranding, BRANDING_FONTS } from '../context/BrandingContext';
import { FloppyDisk, Upload, X, DownloadSimple, UploadSimple } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HEX_RE = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

const COLOR_FIELDS = [
  { key: 'primary', label: 'Primary' },
  { key: 'primary_hover', label: 'Primary (hover)' },
  { key: 'accent', label: 'Accent' },
  { key: 'accent_hover', label: 'Accent (hover)' },
  { key: 'danger', label: 'Danger' },
  { key: 'danger_hover', label: 'Danger (hover)' },
  { key: 'background', label: 'Background' },
];

const FONT_SLOTS = [
  { key: 'heading', label: 'Police titres', defaultValue: 'Poppins' },
  { key: 'body', label: 'Police corps', defaultValue: 'Inter' },
];

const FONT_PRESETS = [
  { key: 'modern',    label: 'Modern',    heading: 'Poppins',    body: 'Inter' },
  { key: 'editorial', label: 'Editorial', heading: 'Montserrat', body: 'Lato' },
  { key: 'classic',   label: 'Classic',   heading: 'Roboto',     body: 'Open Sans' },
];

const TAGLINE_LOCALES = [
  { key: 'fr', label: 'Tagline (FR)', placeholder: 'Là où Madagascar achète et vend' },
  { key: 'en', label: 'Tagline (EN)', placeholder: 'Where Madagascar buys and sells' },
  { key: 'mg', label: 'Tagline (MG)', placeholder: "Izay hividianan sy hivarotan'i Madagasikara" },
];
const TAGLINE_MAX_LEN = 140;

const ImageField = ({ label, value, onChange, previewClass = 'h-16', purpose = 'listing', onUpload }) => {
  const [uploading, setUploading] = useState(false);
  const fileInputId = `branding-${label.toLowerCase().replace(/\s/g, '-')}`;

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${API}/upload?purpose=${encodeURIComponent(purpose)}`, fd, { withCredentials: true });
      onChange(data.path);
      // Allow callers to inspect the just-uploaded file (e.g. ratio check).
      if (typeof onUpload === 'function') {
        try { onUpload(file, data); } catch { /* ignore */ }
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const previewUrl = value
    ? value.startsWith('http') ? value : `${API}/files/${value}`
    : null;

  return (
    <div data-testid={`branding-${label.toLowerCase().replace(/\s/g, '-')}-field`}>
      <Label className="text-sm">{label}</Label>
      <div className="mt-1 flex items-center gap-3">
        {previewUrl ? (
          <div className="relative shrink-0">
            <img src={previewUrl} alt={label} className={`${previewClass} w-auto object-contain rounded border border-border bg-white`} />
            <button
              type="button"
              onClick={() => onChange(null)}
              className="absolute -top-1.5 -right-1.5 bg-danger text-white rounded-full p-0.5 shadow hover:bg-danger"
              data-testid={`branding-${label.toLowerCase().replace(/\s/g, '-')}-clear`}
              aria-label="Remove"
            >
              <X size={12} weight="bold" />
            </button>
          </div>
        ) : (
          <div className={`${previewClass} w-24 bg-surface_muted border border-dashed border-border rounded flex items-center justify-center text-text_muted text-xs`}>
            Aucun
          </div>
        )}
        <label htmlFor={fileInputId} className="cursor-pointer">
          <span className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline">
            <Upload size={14} /> {uploading ? 'Téléchargement…' : 'Changer'}
          </span>
          <input
            id={fileInputId}
            type="file"
            accept="image/*"
            onChange={handleUpload}
            disabled={uploading}
            className="hidden"
            data-testid={`branding-${label.toLowerCase().replace(/\s/g, '-')}-input`}
          />
        </label>
      </div>
    </div>
  );
};

export const AppearanceManagement = () => {
  const { branding: ctxBranding, refresh } = useBranding();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [bannerRatioInfo, setBannerRatioInfo] = useState(null);

  // Hydrate from the branding context once it loads
  useEffect(() => {
    if (ctxBranding) {
      const ctxTaglines = ctxBranding.taglines || {};
      setForm({
        platform_name: ctxBranding.platform_name || '',
        taglines: {
          fr: ctxTaglines.fr || '',
          en: ctxTaglines.en || '',
          mg: ctxTaglines.mg || '',
        },
        logo_path: ctxBranding.logo_path || null,
        favicon_path: ctxBranding.favicon_path || null,
        banner_path: ctxBranding.banner_path || null,
        banner_headline: ctxBranding.banner_headline || '',
        banner_fit: ctxBranding.banner_fit === 'cover' ? 'cover' : 'contain',
        colors: { ...(ctxBranding.colors || {}) },
        fonts: { heading: 'Poppins', body: 'Inter', ...(ctxBranding.fonts || {}) },
      });
    }
  }, [ctxBranding]);

  if (!form) return null;

  const setField = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));
  const setColor = (k, v) =>
    setForm((prev) => ({ ...prev, colors: { ...prev.colors, [k]: v } }));
  const setTagline = (loc, v) =>
    setForm((prev) => ({ ...prev, taglines: { ...(prev.taglines || {}), [loc]: v } }));

  const handleSave = async () => {
    // Client-side guard on colors (backend also validates)
    for (const { key, label } of COLOR_FIELDS) {
      const c = form.colors?.[key];
      if (c && !HEX_RE.test(c.trim())) {
        toast.error(`Couleur invalide pour « ${label} »: doit être #rgb ou #rrggbb`);
        return;
      }
    }
    setSaving(true);
    try {
      await axios.put(`${API}/admin/branding`, {
        platform_name: form.platform_name,
        taglines: form.taglines,
        logo_path: form.logo_path,
        favicon_path: form.favicon_path,
        banner_path: form.banner_path,
        banner_headline: form.banner_headline,
        banner_fit: form.banner_fit,
        colors: form.colors,
        fonts: form.fonts,
      }, { withCredentials: true });
      await refresh();
      toast.success('Apparence enregistrée');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Échec de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  // Export the current form state (in-memory, before save) as a portable JSON
  // file. Asset paths are relative to whichever backend hosts /api/files/...
  // so cross-environment imports require those files to be reachable.
  const handleExport = () => {
    const payload = {
      platform_name: form.platform_name,
      taglines: form.taglines || {},
      colors: form.colors || {},
      fonts: form.fonts || {},
      logo_path: form.logo_path || null,
      favicon_path: form.favicon_path || null,
      banner_path: form.banner_path || null,
      banner_headline: form.banner_headline || null,
      banner_fit: form.banner_fit || 'contain',
      exported_at: new Date().toISOString(),
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const safeName = (form.platform_name || 'branding').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    a.download = `${safeName || 'branding'}-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Branding exporté');
  };

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-importing the same file
    if (!file) return;
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      if (!data || typeof data !== 'object') throw new Error('Format JSON invalide');
      // Server-side validator (already in place) handles font whitelist + hex
      // checks and returns an HTTP 400 we surface to the user.
      const payload = {
        platform_name: data.platform_name ?? form.platform_name,
        taglines: { ...(form.taglines || {}), ...(data.taglines || {}) },
        logo_path: data.logo_path ?? form.logo_path ?? null,
        favicon_path: data.favicon_path ?? form.favicon_path ?? null,
        banner_path: data.banner_path ?? form.banner_path ?? null,
        banner_headline: data.banner_headline ?? form.banner_headline ?? null,
        banner_fit: data.banner_fit ?? form.banner_fit ?? 'contain',
        colors: { ...(form.colors || {}), ...(data.colors || {}) },
        fonts: { ...(form.fonts || {}), ...(data.fonts || {}) },
      };
      await axios.put(`${API}/admin/branding`, payload, { withCredentials: true });
      await refresh();
      toast.success('Branding importé et appliqué');
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || 'Import échoué';
      toast.error(`Import: ${detail}`);
    }
  };

  return (
    <div data-testid="appearance-management">
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-text_strong">Apparence</h2>
          <p className="text-sm text-text_muted">
            Personnalisez le nom, le logo et les couleurs de la plateforme. Les changements sont appliqués instantanément.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={handleExport}
            className="gap-2 border-border"
            data-testid="branding-export-btn"
            title="Télécharger le branding en JSON"
          >
            <DownloadSimple size={16} weight="bold" />
            Export
          </Button>
          <label className="inline-flex">
            <input
              type="file"
              accept="application/json,.json"
              onChange={handleImportFile}
              className="hidden"
              data-testid="branding-import-input"
            />
            <Button
              type="button"
              variant="outline"
              onClick={(e) => e.currentTarget.previousSibling.click()}
              className="gap-2 border-border"
              data-testid="branding-import-btn"
              title="Importer un fichier JSON de branding"
            >
              <UploadSimple size={16} weight="bold" />
              Import
            </Button>
          </label>
          <Button onClick={handleSave} disabled={saving} variant="primary" className="gap-2" data-testid="branding-save-btn">
            <FloppyDisk size={16} weight="bold" />
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        {/* Identity */}
        <section className="bg-white border border-border rounded-xl p-5 space-y-4" data-testid="branding-identity">
          <h3 className="text-sm font-semibold text-text_main uppercase tracking-wide">Identité</h3>
          <div>
            <Label className="text-sm">Nom de la plateforme</Label>
            <Input
              value={form.platform_name}
              onChange={(e) => setField('platform_name', e.target.value)}
              placeholder="ATÔ"
              className="mt-1 border border-border focus:border-primary focus:ring-1 focus:ring-primary"
              data-testid="branding-platform-name"
              maxLength={60}
            />
          </div>
          <div className="space-y-3" data-testid="branding-taglines">
            <div>
              <Label className="text-sm">Taglines (par langue)</Label>
              <p className="text-[11px] text-text_muted mt-0.5">
                Laissez vide pour utiliser la traduction par défaut. Max {TAGLINE_MAX_LEN} caractères.
              </p>
            </div>
            {TAGLINE_LOCALES.map(({ key, label, placeholder }) => (
              <div key={key}>
                <Label className="text-xs text-text_muted">{label}</Label>
                <Input
                  value={form.taglines?.[key] || ''}
                  onChange={(e) => setTagline(key, e.target.value)}
                  placeholder={placeholder}
                  className="mt-1 border border-border focus:border-primary focus:ring-1 focus:ring-primary"
                  data-testid={`branding-tagline-${key}`}
                  maxLength={TAGLINE_MAX_LEN}
                />
              </div>
            ))}
          </div>
          <ImageField
            label="Logo"
            value={form.logo_path}
            onChange={(v) => setField('logo_path', v)}
            previewClass="h-12"
            purpose="logo"
          />
          <ImageField
            label="Favicon"
            value={form.favicon_path}
            onChange={(v) => setField('favicon_path', v)}
            previewClass="h-10"
            purpose="favicon"
          />
        </section>

        {/* Colors */}
        <section className="bg-white border border-border rounded-xl p-5 space-y-4" data-testid="branding-colors">
          <h3 className="text-sm font-semibold text-text_main uppercase tracking-wide">Couleurs</h3>
          {COLOR_FIELDS.map(({ key, label }) => (
            <div key={key} className="grid grid-cols-[1fr_auto_110px] items-center gap-2">
              <Label className="text-sm">{label}</Label>
              <input
                type="color"
                value={form.colors?.[key] || '#000000'}
                onChange={(e) => setColor(key, e.target.value)}
                className="h-9 w-12 rounded border border-border cursor-pointer"
                data-testid={`branding-color-${key}-picker`}
              />
              <Input
                value={form.colors?.[key] || ''}
                onChange={(e) => setColor(key, e.target.value)}
                placeholder="#2E6F57"
                className="border border-border focus:border-primary focus:ring-1 focus:ring-primary font-mono text-sm"
                data-testid={`branding-color-${key}-hex`}
              />
            </div>
          ))}
          <p className="text-xs text-text_muted">
            Les couleurs sont injectées en variables CSS — tout changement est visible sans recharger.
          </p>
        </section>

        {/* Typography */}
        <section className="bg-white border border-border rounded-xl p-5 space-y-4" data-testid="branding-typography">
          <h3 className="text-sm font-semibold text-text_main uppercase tracking-wide">Typographie</h3>

          {/* Quick presets — apply both fonts at once. Manual dropdowns below stay fully functional. */}
          <div className="space-y-2" data-testid="branding-font-presets">
            <Label className="text-xs text-text_muted uppercase tracking-wide">Préréglages</Label>
            <div className="flex flex-wrap gap-2">
              {FONT_PRESETS.map((p) => {
                const active = form.fonts?.heading === p.heading && form.fonts?.body === p.body;
                return (
                  <button
                    key={p.key}
                    type="button"
                    onClick={() => setForm((prev) => ({
                      ...prev,
                      fonts: { ...(prev.fonts || {}), heading: p.heading, body: p.body },
                    }))}
                    className={`flex flex-col items-start text-left px-3 py-2 rounded-lg border transition-colors ${
                      active
                        ? 'border-primary bg-primary/5 text-primary'
                        : 'border-border bg-white hover:border-primary/40 text-text_main'
                    }`}
                    data-testid={`branding-font-preset-${p.key}`}
                  >
                    <span className="text-sm font-semibold" style={{ fontFamily: `"${p.heading}", system-ui, sans-serif` }}>{p.label}</span>
                    <span className="text-[11px] text-text_muted mt-0.5">{p.heading} + {p.body}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {FONT_SLOTS.map(({ key, label, defaultValue }) => (
            <div key={key} className="grid grid-cols-[1fr_220px] items-center gap-2">
              <Label className="text-sm">{label}</Label>
              <select
                value={form.fonts?.[key] || defaultValue}
                onChange={(e) => setForm((prev) => ({ ...prev, fonts: { ...(prev.fonts || {}), [key]: e.target.value } }))}
                className="h-9 px-2 rounded border border-border bg-white text-sm focus:border-primary focus:ring-1 focus:ring-primary"
                data-testid={`branding-font-${key}-select`}
              >
                {BRANDING_FONTS.map((f) => (
                  <option key={f} value={f} style={{ fontFamily: `"${f}", system-ui, sans-serif` }}>
                    {f}
                  </option>
                ))}
              </select>
            </div>
          ))}
          <p className="text-xs text-text_muted">
            Les polices sont chargées depuis Google Fonts à la volée. Les changements s'appliquent instantanément sur toute la plateforme.
          </p>
        </section>

        {/* Banner */}
        <section className="bg-white border border-border rounded-xl p-5 space-y-4 lg:col-span-2" data-testid="branding-banner">
          <div className="flex items-baseline justify-between flex-wrap gap-2">
            <h3 className="text-sm font-semibold text-text_main uppercase tracking-wide">Bannière d'accueil</h3>
            <span className="text-[11px] text-text_muted" data-testid="branding-banner-recommended">
              Recommandé : 1920×600 px (16:5) • zone sûre : contenu important au centre
            </span>
          </div>

          <ImageField
            label="Image bannière"
            value={form.banner_path}
            onChange={(v) => setField('banner_path', v)}
            previewClass="h-28"
            purpose="banner"
            onUpload={(file) => {
              // Read intrinsic dimensions and warn if far from 16:5.
              const url = URL.createObjectURL(file);
              const img = new Image();
              img.onload = () => {
                const w = img.naturalWidth, h = img.naturalHeight;
                URL.revokeObjectURL(url);
                if (!w || !h) return;
                const ratio = w / h;
                const target = 16 / 5; // 3.2
                const drift = Math.abs(ratio - target) / target;
                setBannerRatioInfo({ w, h, ratio, drift });
                if (drift > 0.20) {
                  toast.warning(
                    `Image ${w}×${h} (${ratio.toFixed(2)}:1) — recommandé 1920×600 (3.2:1). Le rendu peut paraître étiré ou rogné.`
                  );
                }
              };
              img.onerror = () => URL.revokeObjectURL(url);
              img.src = url;
            }}
          />

          {bannerRatioInfo && (
            <div
              className={`text-[11px] px-2 py-1.5 rounded border ${
                bannerRatioInfo.drift > 0.20
                  ? 'bg-warning-soft text-warning border-warning'
                  : 'bg-success-soft text-success border-success'
              }`}
              data-testid="branding-banner-ratio-status"
            >
              {bannerRatioInfo.drift > 0.20
                ? `⚠ Ratio ${bannerRatioInfo.ratio.toFixed(2)}:1 (image ${bannerRatioInfo.w}×${bannerRatioInfo.h}). Hors zone recommandée 16:5 (3.2:1).`
                : `✓ Ratio ${bannerRatioInfo.ratio.toFixed(2)}:1 (image ${bannerRatioInfo.w}×${bannerRatioInfo.h}). Compatible avec la zone recommandée.`}
            </div>
          )}

          <div>
            <Label className="text-sm">Mode d'affichage</Label>
            <div className="mt-1 flex gap-2" data-testid="branding-banner-fit-toggle">
              {[
                { v: 'contain', label: 'Contain (image entière)', desc: "L'image complète est visible. Recommandé." },
                { v: 'cover',   label: 'Cover (remplir 16:5)',   desc: "Remplit le cadre 16:5. Peut rogner l'image." },
              ].map((opt) => (
                <button
                  key={opt.v}
                  type="button"
                  onClick={() => setField('banner_fit', opt.v)}
                  className={`flex-1 text-left px-3 py-2 rounded-lg border transition-colors text-xs ${
                    form.banner_fit === opt.v
                      ? 'border-primary bg-primary/5 text-primary'
                      : 'border-border bg-white hover:border-primary/40 text-text_main'
                  }`}
                  data-testid={`branding-banner-fit-${opt.v}`}
                >
                  <div className="font-semibold">{opt.label}</div>
                  <div className="text-[10px] text-text_muted mt-0.5">{opt.desc}</div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-sm">Titre (optionnel, affiché sur la bannière)</Label>
            <Input
              value={form.banner_headline}
              onChange={(e) => setField('banner_headline', e.target.value)}
              placeholder="Le n°1 des petites annonces à Madagascar"
              className="mt-1 border border-border focus:border-primary focus:ring-1 focus:ring-primary"
              data-testid="branding-banner-headline"
              maxLength={200}
            />
          </div>

          {/* Multi-device preview */}
          {form.banner_path && (
            <div className="space-y-2" data-testid="branding-banner-preview">
              <Label className="text-xs text-text_muted uppercase tracking-wide">Aperçu</Label>
              <div className="flex flex-wrap gap-3">
                {[
                  { key: 'desktop', label: 'Desktop', width: 'w-72' },
                  { key: 'tablet',  label: 'Tablet',  width: 'w-52' },
                  { key: 'mobile',  label: 'Mobile',  width: 'w-32' },
                ].map((d) => (
                  <div key={d.key} className={`${d.width} space-y-1`} data-testid={`branding-banner-preview-${d.key}`}>
                    <div className="text-[10px] text-text_muted">{d.label}</div>
                    {form.banner_fit === 'cover' ? (
                      <div className="border border-border rounded overflow-hidden bg-cream aspect-[16/5]">
                        <img
                          src={form.banner_path.startsWith('http') ? form.banner_path : `${API}/files/${form.banner_path}`}
                          alt="preview"
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ) : (
                      <div className="border border-border rounded overflow-hidden bg-cream">
                        <img
                          src={form.banner_path.startsWith('http') ? form.banner_path : `${API}/files/${form.banner_path}`}
                          alt="preview"
                          className="w-full h-auto block"
                          style={{ objectFit: 'contain' }}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};
